import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import AddToCalendarSheet from "@/components/calendar/AddToCalendarSheet";
import {
  todayISO, daysFrom, groupByDay, statusMeta, fmtWeekdayShort, fmtDayNum,
  fmtFullDate, fmtTime, isToday,
} from "@/lib/calendar";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Plus, Check, X, BookOpen, ArrowRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";

// Minha Semana (Epic 11.3): a 7-day strip starting today, above the agent grid.
// Empty day → open the add sheet on that day; a day with entries → quick menu
// (mark posted / not posted, open artifact, jump to the calendar day). Hidden for
// viewers with nothing scheduled, and while the backend calendar is still 404.
export default function MinhaSemana({ viewer = false }) {
  const nav = useNavigate();
  const [today] = useState(() => todayISO());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notReady, setNotReady] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(today);
  const [menuDay, setMenuDay] = useState(null);

  const days = useMemo(() => daysFrom(today, 7), [today]);

  const load = async () => {
    setLoading(true);
    setNotReady(false);
    try {
      const { data } = await api.get("/calendar", { params: { from: days[0], to: days[6] } });
      setEntries(data || []);
    } catch (err) {
      if (err.response?.status === 404) setNotReady(true);
      // Any other failure degrades silently: the dashboard strip is a bonus
      // surface and must never block the main page.
      else if (process.env.NODE_ENV !== "production") console.warn("MinhaSemana:", friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => groupByDay(entries), [entries]);

  const setStatus = async (entry, status) => {
    setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status } : e)));
    try {
      await api.patch(`/calendar/${entry.id}`, { status });
    } catch (err) {
      setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status: entry.status } : e)));
      toast.error(friendlyError(err));
    }
  };

  const onDayClick = (day) => {
    if ((grouped[day] || []).length > 0) setMenuDay(day);
    else if (!viewer) { setAddDate(day); setAddOpen(true); }
  };

  // Hide entirely when there is nothing to show and either the user can't add
  // (viewer) or the backend calendar isn't live yet.
  if (loading) return null;
  if (notReady) return null;
  if (viewer && entries.length === 0) return null;

  const menuEntries = menuDay ? grouped[menuDay] || [] : [];

  return (
    <div className="mb-8" data-testid="minha-semana">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-2xl text-brand-ink flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-brand-rose" /> Minha semana
        </h2>
        <button
          onClick={() => nav("/calendario")}
          data-testid="minha-semana-open"
          className="text-[12.5px] font-ui text-brand-ink2 hover:text-brand-rose transition-colors inline-flex items-center gap-1"
        >
          Ver calendário <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex gap-2.5 overflow-x-auto scrollbar-soft pb-1 -mx-1 px-1">
        {days.map((day) => {
          const dayEntries = grouped[day] || [];
          const first = dayEntries[0];
          const cur = isToday(day, today);
          const meta = first ? statusMeta(first, today) : null;
          return (
            <button
              key={day}
              type="button"
              data-testid={`minha-semana-day-${day}`}
              onClick={() => onDayClick(day)}
              className={`shrink-0 w-[116px] text-left rounded-2xl border p-3 transition-colors ${
                cur ? "border-brand-rose bg-brand-rose/5" : "border-brand-line bg-brand-surface hover:border-brand-rose/50"
              }`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className={`text-[10px] tracking-[0.14em] uppercase font-ui ${cur ? "text-brand-rose" : "text-brand-ink3"}`}>
                  {fmtWeekdayShort(day)}
                </span>
                <span className={`font-display text-lg leading-none ${cur ? "text-brand-rose" : "text-brand-ink"}`}>
                  {fmtDayNum(day)}
                </span>
              </div>
              {first ? (
                <div className="space-y-1">
                  <span className={`block truncate rounded px-1.5 py-1 text-[10.5px] leading-tight ${meta.className}`}>
                    {first.title}
                  </span>
                  {dayEntries.length > 1 && (
                    <span className="block text-[10px] text-brand-ink3 font-ui">+{dayEntries.length - 1}</span>
                  )}
                </div>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-brand-ink3 font-ui">
                  {!viewer && <Plus className="w-3 h-3" />} Livre
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Quick menu for a day with entries */}
      <Sheet open={Boolean(menuDay)} onOpenChange={(o) => !o && setMenuDay(null)}>
        <SheetContent side="bottom" data-testid="minha-semana-menu" className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">
              {menuDay ? fmtFullDate(menuDay) : ""}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2.5">
            {menuEntries.map((e) => {
              const meta = statusMeta(e, today);
              return (
                <div key={e.id} className="rounded-2xl border border-brand-line bg-brand-elevated p-3.5">
                  <div className="flex items-center gap-2 mb-2.5">
                    {e.entry_time && <span className="text-[11px] text-brand-ink2 font-ui tabular-nums">{fmtTime(e.entry_time)}</span>}
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10.5px] font-ui ${meta.className}`}>{meta.label}</span>
                  </div>
                  <div className="font-display text-[16px] text-brand-ink leading-tight mb-3">{e.title}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!viewer && (
                      <>
                        <button
                          data-testid={`minha-semana-posted-${e.id}`}
                          onClick={() => setStatus(e, "posted")}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-sage/15 text-brand-sage text-[12px] font-ui hover:bg-brand-sage/25 transition-colors min-h-[40px]"
                        >
                          <Check className="w-3.5 h-3.5" /> Postei
                        </button>
                        <button
                          data-testid={`minha-semana-notposted-${e.id}`}
                          onClick={() => setStatus(e, "not_posted")}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 text-[12px] font-ui hover:text-brand-ink transition-colors min-h-[40px]"
                        >
                          <X className="w-3.5 h-3.5" /> Não postei
                        </button>
                      </>
                    )}
                    {e.artifact && (
                      <button
                        onClick={() => { setMenuDay(null); nav(e.artifact_id ? `/library?artifact=${e.artifact_id}` : "/library"); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 text-[12px] font-ui hover:text-brand-rose transition-colors min-h-[40px]"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Abrir artefato
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            data-testid="minha-semana-seeday"
            onClick={() => { const d = menuDay; setMenuDay(null); nav(`/calendario?view=day&date=${d}`); }}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-brand-line hover:border-brand-rose text-brand-ink2 hover:text-brand-rose rounded-2xl py-3 text-[13px] font-ui transition-colors min-h-[44px]"
          >
            Ver dia no calendário <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </SheetContent>
      </Sheet>

      <AddToCalendarSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultDate={addDate}
        onAdded={load}
      />
    </div>
  );
}
